import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LandingHeader } from "@/components/LandingHeader";
import { DemoVideoSection } from "@/components/DemoVideoSection";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { TrustSignalsSection } from "@/components/TrustSignalsSection";
import { AppFooter } from "@/components/AppFooter";
import { StickyCTA } from "@/components/StickyCTA";
import { SEOMetadata } from "@/components/SEOMetadata";
import { ArrowRight, Download, Play, Shield, Zap, Users } from "lucide-react";
import vewlyLogoTransparent from "@/assets/Logo_transparent2.png";
import { Card, CardContent } from "@/components/ui/card";

import { DynamicHero } from "@/components/DynamicHero";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
        <SEOMetadata />
        <LandingHeader />
        <StickyCTA />
      
        {/* Dynamic Hero Section */}
        <DynamicHero
          className="pt-20"
          title={
            <>
              Your Media,
              <br />
              <span className="bg-gradient-to-r from-primary via-primary/80 to-primary/50 bg-clip-text text-transparent">
                Your Way
              </span>
            </>
          }
          subtitle="Self-hosted streaming platform with enterprise features. Watch anywhere, anytime, on any device."
          backgroundImage="/hero-cinematic-bg.jpg"
          overlay="gradient"
          height="xl"
        >
          <div className="flex items-center justify-center mb-8">
            <img 
              src={vewlyLogoTransparent} 
              alt="Vewly" 
              width="160"
              height="160"
              className="h-32 w-32 md:h-40 md:w-40 drop-shadow-2xl elevation-6 animate-scale-in"
            />
          </div>
          
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <Button 
              size="lg" 
              className="sm:h-12 sm:px-10 elevation-4 hover-lift hover:shadow-glow" 
              asChild
            >
              <Link to="/auth?tier=free">
                Start Your 7-Day Free Trial with 4K Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Link to="/public-downloads">
              <Button 
                size="lg" 
                className="sm:h-12 sm:px-10 glass elevation-3 hover-lift" 
                variant="outline"
              >
                <Download className="mr-2 h-5 w-5" />
                Download Now
              </Button>
            </Link>
          </div>
        </DynamicHero>

        <TrustSignalsSection />
        <DemoVideoSection />

        {/* Value Pillars Section */}
        <section id="features" className="py-24 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Powerful Features Built for You
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Everything you need to manage, stream, and enjoy your content safely across all devices
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <Card className="elevation-4 hover-lift group border-border/50 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardContent className="pt-6 text-center relative z-10">
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full scale-150 group-hover:scale-175 transition-transform duration-500" />
                    <Shield className="h-16 w-16 text-primary relative z-10 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 group-hover:text-primary transition-colors duration-300">Privacy First</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Self-hosted means your data stays yours. Enterprise-grade security with GDPR/CCPA compliance, 
                    WebAuthn MFA, and end-to-end encryption. Bank-level security for your personal media.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="elevation-4 hover-lift group border-border/50 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardContent className="pt-6 text-center relative z-10">
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full scale-150 group-hover:scale-175 transition-transform duration-500" />
                    <Zap className="h-16 w-16 text-primary relative z-10 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 group-hover:text-primary transition-colors duration-300">Simplicity</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    One-tap setup wizard gets you streaming in minutes. Intuitive interface works seamlessly 
                    on web, mobile, TV, and desktop. No technical expertise required.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="elevation-5 hover-lift group border-primary/50 overflow-hidden relative scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent" />
                <CardContent className="pt-6 text-center relative z-10">
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-primary/30 blur-xl rounded-full scale-150 group-hover:scale-175 transition-transform duration-500" />
                    <Users className="h-16 w-16 text-primary relative z-10 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-primary">AI Family-Safe with 4K Streaming for Pro Users</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Smart AI-powered content discovery and family-safe filtering. <strong>4K+ streaming with hardware 
                    transcoding exclusively for Pro Pass subscribers.</strong> 1080p for Free tier.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-24">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Free forever for personal use. Scale when you need to.
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-7xl mx-auto">
              <Card className="relative elevation-3 hover-lift border-border/50 overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-muted/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardContent className="pt-6 relative z-10">
                  <h3 className="text-2xl font-bold mb-2">Freemium</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">$0</span>
                    <span className="text-muted-foreground">/forever</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>7-day unlimited trial</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>2 hours/day after trial</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>1 concurrent stream</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>2GB storage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>1080p playback</span>
                    </li>
                  </ul>
                  <Link to="/auth?tier=free">
                    <Button variant="outline" className="w-full">Start Free Trial</Button>
                  </Link>
                </CardContent>
              </Card>
              
              <Card className="relative border-primary/70 elevation-6 scale-105 hover-lift overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent" />
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-3 py-1 rounded-bl text-sm font-semibold elevation-3 animate-pulse">
                  Popular
                </div>
                <CardContent className="pt-6 relative z-10">
                  <h3 className="text-2xl font-bold mb-2">Pro Pass</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">$7.99</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Unlimited streams</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>10GB storage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>4K+ streaming</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Hardware transcoding</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>WebAuthn MFA</span>
                    </li>
                  </ul>
                  <Link to="/auth?tier=pro&interval=month">
                    <Button className="w-full">Choose Pro Pass</Button>
                  </Link>
                </CardContent>
              </Card>
              
              <Card className="relative elevation-4 hover-lift border-primary/30 overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardContent className="pt-6 relative z-10">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-primary transition-colors duration-300">Lifetime Pass</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">$299</span>
                    <span className="text-muted-foreground"> one-time</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>All Pro features</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Lifetime access</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Beta plugin access</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Founder badge</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Limited-time offer</span>
                    </li>
                  </ul>
                  <Link to="/auth?tier=lifetime">
                    <Button variant="outline" className="w-full">Get Lifetime Pass</Button>
                  </Link>
                </CardContent>
              </Card>

              <Card className="relative elevation-3 hover-lift border-border/50 overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardContent className="pt-6 relative z-10">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-primary transition-colors duration-300">Developer Tools</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">$29</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Advanced analytics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>A/B testing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Revenue insights</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Priority review</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">✓</span>
                      <span>Developer API</span>
                    </li>
                  </ul>
                  <Link to="/auth?tier=developer&interval=month">
                    <Button variant="outline" className="w-full">Get Developer Tools</Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <TestimonialsSection />
        
        <AppFooter />
      </div>
  );
}
